/**
 * SR-301 phase 8 (B1) — an N-slot categorical colour ramp for id/key-derived
 * colour (a zone, a user, a product type), never a semantic status. Retires
 * 4 copies of one 6-hex array plus one keyed map across
 * zones.tsx/selectors.tsx/user-groups.tsx — see the phase 8 doc §2a.
 *
 * Three functions, one per selection strategy measured on disk. Deliberately
 * plain functions, not React hooks (no `use` prefix): every one of the three
 * measured call sites resolves a colour inside `.map()` over a list (a zone
 * card, a table row, a chip), and a `use`-prefixed export invites exactly the
 * conditional/loop hook call `eslint-plugin-react-hooks` rejects. There is no
 * state or effect here to justify the hook form.
 */

/** One of the six categorical slots. Matches `--pallet-role-categorical-1`
 *  through `-6` in tokens.css — see that file for the palette itself. */
export type CategoricalSlot = 1 | 2 | 3 | 4 | 5 | 6;

const SLOT_COUNT = 6;

/** Strategy 1: cycle by a stable numeric id, e.g. a zone's own id — so a
 *  renamed entity keeps its colour. Matches the retired `ZONE_COLORS[id %
 *  length]` idiom. */
export function categoricalSlotByIndex(index: number): CategoricalSlot {
  const zeroBased = ((index % SLOT_COUNT) + SLOT_COUNT) % SLOT_COUNT;
  return (zeroBased + 1) as CategoricalSlot;
}

/** Strategy 2: hash a string key (e.g. a user id) into a slot. Matches the
 *  retired `AVATAR_COLORS[hash % length]` idiom, character-code-sum hash and
 *  all — changing the hash function would reshuffle every already-assigned
 *  colour for no benefit. */
export function categoricalSlotByHash(key: string): CategoricalSlot {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash += key.charCodeAt(i);
  return categoricalSlotByIndex(hash);
}

/** Strategy 3: look a key up in a caller-supplied map (typically built once
 *  per render from a list the key-only caller doesn't have, e.g. zone name ->
 *  slot built from the zone list's ids), with a fallback for a key the map
 *  doesn't have — a stale name whose entity is gone. Matches
 *  `user-groups.tsx`'s `zoneColorByName` idiom, minus the ad hoc `#6b7280`
 *  literal fallback it used to carry (retired — see the phase 8 report). */
export function categoricalSlotByKey<K extends string>(
  key: K,
  map: ReadonlyMap<K, CategoricalSlot>,
  fallback: CategoricalSlot,
): CategoricalSlot {
  return map.get(key) ?? fallback;
}

export interface CategoricalColorVars {
  /** The slot's foreground — text/icon/border colour. */
  color: string;
  /** The slot's paired tint — background for a container-shaped surface. */
  containerColor: string;
}

/** Resolves a slot to its two paired CSS custom-property references. Never
 *  a literal hex: the retired call sites each computed one at runtime
 *  (`` `${color}1e` ``); the tokens these reference are the phase 8 (R3)
 *  replacement. */
export function categoricalColorVars(
  slot: CategoricalSlot,
): CategoricalColorVars {
  return {
    color: `var(--pallet-role-categorical-${slot})`,
    containerColor: `var(--pallet-role-categorical-${slot}-container)`,
  };
}
